import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import PaymentForm from "@/pages/admin/PaymentForm";
import StatusBadge from "@/components/StatusBadge";
import { useAdminData } from "@/pages/admin/useAdminData";
import { client, errMsg } from "@/lib/api";
import { money } from "@/lib/format";

export default function PaymentsTab() {
  const { families, payments, reload } = useAdminData();
  const [editing, setEditing] = useState(null);
  const api = client("admin");
  const save = async (p) => {
    try {
      if (editing === "new") await api.post("/admin/payments", p); else await api.put(`/admin/payments/${editing.id}`, p);
      toast.success("Payment saved"); setEditing(null); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (p) => { if (!window.confirm("Delete this payment?")) return; await api.delete(`/admin/payments/${p.id}`); toast.success("Payment deleted"); reload(); };
  const link = async (p, family_id) => {
    if (!family_id) return;
    try { await api.put(`/admin/payments/${p.id}`, { ...p, family_id }); toast.success("Payment linked to family"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const ids = new Set(families.map((f) => f.id));
  const unlinked = payments.filter((p) => !ids.has(p.family_id));
  const owing = families.filter((f) => f.balance > 0);
  const total = payments.reduce((s, p) => s + Number(p.amount || 0), 0);

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="text-3xl font-bold text-amber-950">Money entries</h2>
          <button data-testid="add-payment-button" onClick={() => setEditing("new")} className="btn-primary"><Plus className="w-5 h-5" />Record payment</button></div>
        <p data-testid="payments-total" className="text-xl">Total collected: <b className="font-heading text-2xl text-[#2B5338]">{money(total)}</b></p>
        {editing && <div className="card-warm" data-testid="payment-editor"><PaymentForm key={editing.id || "new"} initial={editing === "new" ? null : editing} families={families} onSave={save} onCancel={() => setEditing(null)} /></div>}
        {unlinked.length > 0 && (
          <div className="card-warm !bg-amber-50 !border-amber-300 space-y-3" data-testid="unlinked-payments">
            <h3 className="text-2xl font-bold text-amber-900">Not linked to a family yet ({unlinked.length})</h3>
            <p className="text-stone-700">Once they register, pick their family to count the payment toward their balance.</p>
            {unlinked.map((p) => (
              <div key={p.id} data-testid={`unlinked-row-${p.id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl bg-white p-3 border border-amber-200">
                <div className="flex-1 min-w-0"><p className="font-semibold text-lg">{p.payer_name || "(no name)"} · {money(p.amount)}</p><p className="text-sm text-stone-600">{p.date} · {p.method}{p.note && ` · ${p.note}`}</p></div>
                <select data-testid={`link-family-select-${p.id}`} className="field sm:max-w-[260px]" value="" onChange={(e) => link(p, e.target.value)}>
                  <option value="">Link to family…</option>
                  {families.map((f) => <option key={f.id} value={f.id}>{f.family_name}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
        <ul className="space-y-2" data-testid="payments-list">
          {payments.map((p) => (
            <li key={p.id} data-testid={`payment-row-${p.id}`} className="card-warm !p-4 flex items-center gap-3">
              <div className="flex-1 min-w-0"><p className="font-semibold text-lg">{p.family_name || <span className="text-amber-800">{p.payer_name} (not linked)</span>}</p><p className="text-stone-600 text-sm truncate">{p.date} · {p.method}{p.note && ` · ${p.note}`}</p></div>
              <b className="font-heading text-xl">{money(p.amount)}</b>
              <button data-testid={`payment-edit-${p.id}`} onClick={() => setEditing(p)} className="p-2 rounded-full hover:bg-stone-100" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`payment-delete-${p.id}`} onClick={() => remove(p)} className="p-2 rounded-full text-red-700 hover:bg-red-50" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
          {!payments.length && <p className="text-stone-600">No payments recorded yet.</p>}
        </ul>
      </div>
      <div className="card-warm h-fit space-y-3" data-testid="owing-list">
        <h3 className="text-2xl font-bold text-amber-900">Still owing ({owing.length})</h3>
        {owing.map((f) => <div key={f.id} className="flex items-center justify-between gap-2 py-2 border-b border-amber-900/10"><div><p className="font-semibold">{f.family_name}</p><StatusBadge status={f.pay_status} /></div><b>{money(f.balance)}</b></div>)}
        {!owing.length && <p className="text-stone-600">Everyone is paid up.</p>}
      </div>
    </div>
  );
}
